import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import FormInput from '../../components/shared/FormInput';
import ThemedSelect from '../../components/shared/ThemedSelect';
import { TRADE_SESSION_PRESETS, tradeSessionActive } from '../engine/tradeSession';

const sectionStyle = (colors) => ({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: colors.textMuted,
  marginBottom: 2,
});

function groupSchema(schema) {
  const groups = [];
  for (const field of schema) {
    const section = field.section || '';
    const last = groups[groups.length - 1];
    if (!last || last.section !== section) groups.push({ section, fields: [field] });
    else last.fields.push(field);
  }
  return groups;
}

function ParamRow({ label, children, colors }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        minWidth: 0,
        minHeight: 28,
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 550,
          color: colors.text,
          lineHeight: 1.25,
          minWidth: 0,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function fieldInputStyle(field, compact) {
  const isTime = field.type === 'time';
  return {
    width: isTime ? 112 : 76,
    flexShrink: 0,
    height: compact ? 28 : 26,
    padding: '2px 8px',
    textAlign: isTime ? 'left' : 'right',
    fontVariantNumeric: 'tabular-nums',
  };
}

export function TradeSessionFields({ value, onChange }) {
  const { colors } = useTheme();
  const preset = value?.preset || 'any';
  const custom = preset === 'custom';

  const patch = (next) => onChange?.({ ...value, ...next });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={sectionStyle(colors)}>Session</div>
      <ParamRow label="Window" colors={colors}>
        <ThemedSelect
          aria-label="Trade session"
          value={preset}
          options={TRADE_SESSION_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          onChange={(next) => patch({ preset: next })}
          width={128}
          height={26}
          fontSize="0.75rem"
        />
      </ParamRow>
      {custom ? (
        <>
          <ParamRow label="Start" colors={colors}>
            <FormInput
              type="time"
              step={60}
              aria-label="Session start"
              value={value?.start || '09:30'}
              onChange={(e) => patch({ start: e.target.value })}
              style={fieldInputStyle({ type: 'time' }, false)}
            />
          </ParamRow>
          <ParamRow label="End" colors={colors}>
            <FormInput
              type="time"
              step={60}
              aria-label="Session end"
              value={value?.end || '16:00'}
              onChange={(e) => patch({ end: e.target.value })}
              style={fieldInputStyle({ type: 'time' }, false)}
            />
          </ParamRow>
        </>
      ) : null}
      {tradeSessionActive(value) ? (
        <div style={{ fontSize: 11, color: colors.textMuted, lineHeight: 1.35, paddingTop: 2 }}>
          New entries only inside this window (ET).
        </div>
      ) : null}
    </div>
  );
}

export default function StrategyParams({ schema = [], values = {}, onChange, compact = false, session, onSessionChange }) {
  const { colors } = useTheme();
  const groups = groupSchema(schema);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {session ? <TradeSessionFields value={session} onChange={onSessionChange} /> : null}
      {groups.map((group) => (
        <div key={group.section || 'default'} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {group.section ? <div style={sectionStyle(colors)}>{group.section}</div> : null}
          {group.fields.map((field) => {
            const isTime = field.type === 'time';
            return (
              <ParamRow key={field.key} label={field.label} colors={colors}>
                <FormInput
                  type={isTime ? 'time' : 'number'}
                  min={isTime ? undefined : field.min}
                  max={isTime ? undefined : field.max}
                  step={isTime ? 60 : field.step}
                  value={values[field.key] ?? field.default ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (isTime) {
                      onChange?.(field.key, raw);
                      return;
                    }
                    onChange?.(field.key, raw === '' ? '' : Number(raw));
                  }}
                  style={fieldInputStyle(field, compact)}
                />
              </ParamRow>
            );
          })}
        </div>
      ))}
    </div>
  );
}
